import React, { useEffect, useState, Component } from 'react';
import { motion } from 'framer-motion';
import { Wind, Rocket, ShieldCheck, Send, CheckCircle2, HelpCircle } from 'lucide-react';
import { UtmCapture } from '../components/UtmCapture';
import { getUtmParams } from '../utils/utmTracking';
import { sendWingFoilLeadToMake } from '../utils/makeWebhook';
export function WingFoilCampaignPage() {
  const [formData, setFormData] = useState({
    fullName: '',
    phone: '',
    email: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  useEffect(() => {
    document.title = "Wing Foil - בואו לעוף על המים | ביץ' קלאב מעגן";
  }, []);
  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.fullName.trim() || !formData.phone.trim()) return;
    setIsSubmitting(true);
    // Capture UTM params at submission time
    const utmParams = getUtmParams();
    // Track Meta Pixel custom event for this landing page's lead
    if (typeof window !== 'undefined' && (window as any).fbq) {
      ;
      (window as any).fbq('trackCustom', 'Lead_WingFoil_LP', {
        content_name: 'Wing Foil Campaign',
        currency: 'ILS'
      });
    }
    // Send the lead to Make.com (name/phone/email + UTM attribution).
    // We don't block the user experience on webhook success — if it fails
    // we still show the success state and open WhatsApp.
    await sendWingFoilLeadToMake({
      type: 'ליד Wing Foil',
      source: 'טופס קמפיין ווינג פויל',
      campaign: 'Wing Foil Campaign',
      fullName: formData.fullName.trim(),
      phone: formData.phone.trim(),
      email: formData.email.trim(),
      submittedAt: new Date().toISOString(),
      utm_source: utmParams.utm_source,
      utm_medium: utmParams.utm_medium,
      utm_campaign: utmParams.utm_campaign,
      utm_content: utmParams.utm_content,
      utm_term: utmParams.utm_term,
      utm_id: utmParams.utm_id,
      fbclid: utmParams.fbclid
    });
    setIsSubmitting(false);
    setIsSubmitted(true);
    // Redirect to WhatsApp after successful submission
    const text = encodeURIComponent(`היי, השארתי פרטים בדף הקמפיין של הווינג פויל.\nשם: ${formData.fullName}\nטלפון: ${formData.phone}`);
    window.open(`https://wa.me/972548800260?text=${text}`, '_blank');
  };
  return <div className="min-h-screen bg-white" dir="rtl" data-id="element-2349" data-mp="30li8lock9jlh">
      <UtmCapture data-id="element-2350" data-mp="3edeu39jq4v8w" />

      {/* Minimal Header */}
      <header className="absolute top-0 left-0 right-0 z-50 p-6" data-id="element-2351" data-mp="3ef9g6r6qetek">
        <div className="container mx-auto flex justify-center md:justify-start" data-id="element-2352" data-mp="39fdc6ry1b1z0">
          <img src="https://cdn.magicpatterns.com/uploads/bHRRxzcgoWuLZVsSMLwyGt/logo.png" alt="Beach Club Logo" className="h-12 md:h-16 brightness-0 invert" data-id="element-2353" data-mp="3oajlbnsffgwk" />
        </div>
      </header>

      {/* Hero Section with Form */}
      <section className="relative min-h-[90vh] flex items-center pt-24 pb-12" data-id="element-2354" data-mp="6t5joc6ve0wc">
        <div className="absolute inset-0 z-0" data-id="element-2355" data-mp="at7mioocd0d7">
          <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/co1SZT3RTYumCS8rU85uzt/IMG-20250502-WA0017.jpg" alt="Wing Foil" className="w-full h-full object-cover" style={{
          objectPosition: '60% 40%'
        }} data-id="element-2356" data-mp="arwkatajo0o7" />
          <div className="absolute inset-0 bg-gradient-to-l from-black/90 via-black/60 to-black/20" data-id="element-2357" data-mp="3j9ixbulryfzj"></div>
        </div>

        <div className="container mx-auto px-4 relative z-10" data-id="element-2358" data-mp="3o1su9ulb4gbb">
          <div className="grid md:grid-cols-2 gap-12 items-center" data-id="element-2359" data-mp="3a9cq2qgo6c8t">
            {/* Text Content */}
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6
          }} className="text-white" data-id="element-2360" data-mp="3f87gadgyv32y">
              <div className="inline-flex items-center gap-2 bg-cyan-500/20 border border-cyan-400/30 px-4 py-1.5 rounded-full mb-6 backdrop-blur-sm" data-id="element-2361" data-mp="35986grkgt1ed">
                <Wind className="w-4 h-4 text-cyan-300" data-id="element-2362" data-mp="chm0ddvc43a1" />
                <span className="text-cyan-100 text-sm font-medium" data-id="element-2363" data-mp="7i10n18cbqw9">
                  הספורט הימי שמשגע את העולם
                </span>
              </div>
              <h1 className="text-5xl md:text-7xl font-extrabold mb-6 leading-tight" data-id="element-2364" data-mp="3twke8vphr21o">
                בואו לעוף <br data-id="element-2365" data-mp="3owy2kqpb9tuz" />
                <span className="text-cyan-400" data-id="element-2366" data-mp="3oxg5u7ae58ce">על המים</span>
              </h1>
              <p className="text-xl md:text-2xl text-gray-200 mb-8 max-w-lg leading-relaxed font-light" data-id="element-2367" data-mp="3kvukrynpdnr4">
                הצטרפו אלינו לביץ' קלאב מעגן ותגלו את תחושת הריחוף הממכרת של
                ה-Wing Foil. הדרכות למתחילים, ציוד חדיש וחוף מושלם.
              </p>

              <div className="space-y-4 hidden md:block" data-id="element-2368" data-mp="3azjafe7ob7zb">
                <div className="flex items-center gap-3" data-id="element-2369" data-mp="1e43n0yspgchm">
                  <CheckCircle2 className="w-6 h-6 text-cyan-400" data-id="element-2370" data-mp="19622e9fms541" />
                  <span className="text-lg" data-id="element-2371" data-mp="196v06kwm3ka1">ציוד קל ובטיחותי ללמידה מהירה</span>
                </div>
                <div className="flex items-center gap-3" data-id="element-2372" data-mp="1gpb37ea2mvhh">
                  <CheckCircle2 className="w-6 h-6 text-cyan-400" data-id="element-2373" data-mp="1lnw1h85rj53a" />
                  <span className="text-lg" data-id="element-2374" data-mp="1qfe2fw7sd5o9">הדרכה מקצועית צמודה</span>
                </div>
                <div className="flex items-center gap-3" data-id="element-2375" data-mp="1qhqksahghi1u">
                  <CheckCircle2 className="w-6 h-6 text-cyan-400" data-id="element-2376" data-mp="1vd5x2xztf5w5" />
                  <span className="text-lg" data-id="element-2377" data-mp="xr8fr3x696ls">תנאי רוח אידיאליים בכנרת</span>
                </div>
              </div>
            </motion.div>

            {/* Lead Form */}
            <motion.div initial={{
            opacity: 0,
            scale: 0.95
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.6,
            delay: 0.2
          }} className="bg-white/10 backdrop-blur-md border border-white/20 p-8 rounded-3xl shadow-2xl max-w-md w-full mx-auto md:mx-0" data-id="element-2378" data-mp="12rmq4sm8wsmz">
              {isSubmitted ? <div className="text-center py-8" data-id="element-2379" data-mp="2o592iat3huhz">
                  <div className="w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-2380" data-mp="o03ukyubb3al">
                    <CheckCircle2 className="w-8 h-8 text-emerald-400" data-id="element-2381" data-mp="j10pxfi8uz35" />
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-2" data-id="element-2382" data-mp="x0a4ybuprn5j">
                    איזה כיף!
                  </h3>
                  <p className="text-gray-200" data-id="element-2383" data-mp="t0rl7jempi4a">
                    הפרטים התקבלו בהצלחה. נחזור אלייך בהקדם עם כל המידע.
                  </p>
                </div> : <>
                  <h3 className="text-2xl font-bold text-white mb-2 text-center" data-id="element-2384" data-mp="clwklkgf8z6">
                    רוצה לשמוע עוד?
                  </h3>
                  <p className="text-gray-300 text-center mb-6 text-sm" data-id="element-2385" data-mp="e4k0mhzb6eoh">
                    השאירו פרטים ונחזור אליכם עם כל המידע על שיעורי ההיכרות שלנו
                  </p>

                  <form onSubmit={handleSubmit} className="space-y-4" data-id="element-2386" data-mp="3hpzh2h418uuj">
                    <div data-id="element-2387" data-mp="3dp5ubr2btm38">
                      <input type="text" name="fullName" value={formData.fullName} onChange={handleFormChange} required placeholder="שם מלא" className="w-full bg-white/90 border-0 rounded-xl py-3 px-4 text-gray-900 placeholder:text-gray-500 focus:ring-2 focus:ring-cyan-400 transition-all" data-id="element-2388" data-mp="3dpyrq1feaz8p" />
                    </div>
                    <div data-id="element-2389" data-mp="2tm9lsqa68nnt">
                      <input type="tel" name="phone" value={formData.phone} onChange={handleFormChange} required dir="ltr" placeholder="טלפון" className="w-full bg-white/90 border-0 rounded-xl py-3 px-4 text-gray-900 placeholder:text-gray-500 focus:ring-2 focus:ring-cyan-400 transition-all text-right" data-id="element-2390" data-mp="2ymxk7x0qc6s2" />
                    </div>
                    <div data-id="element-2391" data-mp="3cdkdh9nq14gr">
                      <input type="email" name="email" value={formData.email} onChange={handleFormChange} dir="ltr" placeholder="אימייל (אופציונלי)" className="w-full bg-white/90 border-0 rounded-xl py-3 px-4 text-gray-900 placeholder:text-gray-500 focus:ring-2 focus:ring-cyan-400 transition-all text-right" data-id="element-2392" data-mp="3cec8ojdep06i" />
                    </div>

                    <button type="submit" disabled={isSubmitting} className="w-full bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-400 hover:to-blue-400 text-white font-bold py-4 rounded-xl transition-all shadow-lg flex items-center justify-center gap-2 mt-2" data-id="element-2393" data-mp="1n7plol23me3b">
                      {isSubmitting ? <div className="w-6 h-6 border-2 border-white/30 border-t-white rounded-full animate-spin" data-id="element-2394" data-mp="1987s7sduycnm"></div> : <>
                          <Send className="w-5 h-5" data-id="element-2395" data-mp="25yqib2fqxxjp" />
                          דברו איתי!
                        </>}
                    </button>
                  </form>
                </>}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Quick Benefits */}
      <section className="py-16 bg-gray-50" data-id="element-2396" data-mp="1s7c02xtny0dp">
        <div className="container mx-auto px-4" data-id="element-2397" data-mp="1s6kbi6yhh2wx">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto" data-id="element-2398" data-mp="2ooq3he8gushu">
            <div className="bg-white p-6 rounded-2xl shadow-sm text-center border border-gray-100" data-id="element-2399" data-mp="2jy9nbmd9gsrs">
              <div className="w-12 h-12 bg-cyan-100 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-2400" data-mp="6flr8b5ex4jg">
                <Rocket className="w-6 h-6 text-cyan-600" data-id="element-2401" data-mp="bef88djwrdod" />
              </div>
              <h3 className="text-xl font-bold mb-2 text-gray-800" data-id="element-2402" data-mp="bgjg16kxgwtx">
                למידה מהירה
              </h3>
              <p className="text-gray-600" data-id="element-2403" data-mp="kf4zd3y84fvr">
                עקומת למידה קלה יחסית לגלישת רוח או קייט. תוך מספר שיעורים כבר
                תרגישו את הריחוף.
              </p>
            </div>
            <div className="bg-white p-6 rounded-2xl shadow-sm text-center border border-gray-100" data-id="element-2404" data-mp="udkmkvu4r4zz">
              <div className="w-12 h-12 bg-cyan-100 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-2405" data-mp="z4j7y9tz6ta3">
                <ShieldCheck className="w-6 h-6 text-cyan-600" data-id="element-2406" data-mp="136e5r4cp6oj4" />
              </div>
              <h3 className="text-xl font-bold mb-2 text-gray-800" data-id="element-2407" data-mp="1857mr6r70xo1">
                בטיחות מעל הכל
              </h3>
              <p className="text-gray-600" data-id="element-2408" data-mp="1d3it37ia4q1i">
                הכנף מוחזקת ביד ולא מחוברת לגוף, מה שהופך את הספורט לבטוח מאוד
                וקל לשליטה.
              </p>
            </div>
            <div className="bg-white p-6 rounded-2xl shadow-sm text-center border border-gray-100" data-id="element-2409" data-mp="ljyndkdx08tk">
              <div className="w-12 h-12 bg-cyan-100 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-2410" data-mp="pm200ccbljgf">
                <Wind className="w-6 h-6 text-cyan-600" data-id="element-2411" data-mp="ucs7bv4b3wdr" />
              </div>
              <h3 className="text-xl font-bold mb-2 text-gray-800" data-id="element-2412" data-mp="zbvdxjzxqanu">
                תנאים מושלמים
              </h3>
              <p className="text-gray-600" data-id="element-2413" data-mp="14a6knluxo51u">
                רוחות הצהריים היציבות של הכנרת מספקות את התנאים האידיאליים ביותר
                ללמידה.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-white" data-id="element-2414" data-mp="1d9a6gxlhiyk1">
        <div className="container mx-auto px-4" data-id="element-2415" data-mp="3b1o0zmiychh9">
          <div className="max-w-3xl mx-auto" data-id="element-2416" data-mp="3b38sf39yzkaw">
            <div className="text-center mb-12" data-id="element-2417" data-mp="3f27wymrshf1t">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-cyan-50 rounded-full mb-4" data-id="element-2418" data-mp="3f311dh39zsgs">
                <HelpCircle className="w-8 h-8 text-cyan-600" data-id="element-2419" data-mp="37l2yhvflnfmj" />
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4" data-id="element-2420" data-mp="32r74t56dcex6">
                כל מה שצריך לדעת
              </h2>
              <p className="text-xl text-gray-600" data-id="element-2421" data-mp="2trtphvvkcw4j">
                שאלות ותשובות על הדרכות הווינג פויל שלנו
              </p>
            </div>

            <div className="space-y-6" data-id="element-2422" data-mp="3qgt3lritws4n">
              <div className="bg-gray-50 rounded-2xl p-6 md:p-8 border border-gray-100" data-id="element-2423" data-mp="3qfqbjg9rsjn5">
                <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-3" data-id="element-2424" data-mp="3lgwzzte1n2pm">
                  <span className="w-8 h-8 rounded-full bg-cyan-100 text-cyan-700 flex items-center justify-center shrink-0 text-sm" data-id="element-2425" data-mp="3hjiotw6sykuz">
                    1
                  </span>
                  איך בנויה ההדרכה?
                </h3>
                <p className="text-gray-700 leading-relaxed pr-11" data-id="element-2426" data-mp="212mvna720upm">
                  ההדרכה שלנו משלבת חלק תיאורטי ומעשי, ובמהלכה לומדים את יסודות
                  הגלישה עם ווינג בצורה בטוחה וכיפית. משך כל הדרכה הוא כשעה
                  וחצי.
                </p>
              </div>

              <div className="bg-gray-50 rounded-2xl p-6 md:p-8 border border-gray-100" data-id="element-2427" data-mp="1tjx3swkadie2">
                <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-3" data-id="element-2428" data-mp="1xl8lmfwx4zbp">
                  <span className="w-8 h-8 rounded-full bg-cyan-100 text-cyan-700 flex items-center justify-center shrink-0 text-sm" data-id="element-2429" data-mp="1xn347nkpwfcm">
                    2
                  </span>
                  כמה מפגשים צריך כדי לגלוש לבד?
                </h3>
                <p className="text-gray-700 leading-relaxed pr-11" data-id="element-2430" data-mp="19wpg285970nb">
                  ברוב המקרים, כ־3 מפגשים מספיקים כדי להגיע לרמה שמאפשרת גלישה
                  עצמאית ובטוחה.
                </p>
              </div>

              <div className="bg-gray-50 rounded-2xl p-6 md:p-8 border border-gray-100" data-id="element-2431" data-mp="10vrio8pblrdp">
                <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-3" data-id="element-2432" data-mp="15tj90yabh5fs">
                  <span className="w-8 h-8 rounded-full bg-cyan-100 text-cyan-700 flex items-center justify-center shrink-0 text-sm" data-id="element-2433" data-mp="m929tg96i1un">
                    3
                  </span>
                  האם צריך להביא ציוד?
                </h3>
                <p className="text-gray-700 leading-relaxed pr-11" data-id="element-2434" data-mp="v6mekz692ga0">
                  ממש לא! ההדרכה כוללת את כל הציוד הנדרש: ווינג, גלשן פויל,
                  חליפת הצלה וקסדה.
                </p>
              </div>

              <div className="bg-gray-50 rounded-2xl p-6 md:p-8 border border-gray-100" data-id="element-2435" data-mp="108brkkd1940q">
                <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-3" data-id="element-2436" data-mp="1ewonjhc4emv8">
                  <span className="w-8 h-8 rounded-full bg-cyan-100 text-cyan-700 flex items-center justify-center shrink-0 text-sm" data-id="element-2437" data-mp="19xv65dtpqbps">
                    4
                  </span>
                  מה כן צריך להביא?
                </h3>
                <p className="text-gray-700 leading-relaxed pr-11" data-id="element-2438" data-mp="1izkxkjnlhank">
                  רק בגדים להחלפה ומגבת. אנחנו נדאג לכל השאר.
                </p>
              </div>
            </div>

            <div className="mt-12 text-center bg-cyan-50 rounded-3xl p-8 border border-cyan-100" data-id="element-2439" data-mp="3id1ph6zkldma">
              <h3 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-2440" data-mp="3n4bblxlwtytf">
                נשמע מעניין?
              </h3>
              <p className="text-lg text-gray-700 mb-6" data-id="element-2441" data-mp="3r4v8r16hh2fk">
                השאירו פרטים בטופס למעלה ונקבע לכם מועד שמתאים בדיוק לרוח!
              </p>
              <button onClick={() => window.scrollTo({
              top: 0,
              behavior: 'smooth'
            })} className="bg-cyan-600 hover:bg-cyan-700 text-white font-bold py-3 px-8 rounded-full transition-colors shadow-md" data-id="element-2442" data-mp="2zez5j6lvq6vc">
                חזרה להשארת פרטים ↑
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>;
}